import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
    Paper, Box, Stack, Typography, Divider, Button, Chip
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import VolunteerActivismIcon from '@mui/icons-material/VolunteerActivism';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PlaceIcon from '@mui/icons-material/Place';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import EventRepeatIcon from '@mui/icons-material/EventRepeat';
import { logEvent } from '../analytics';
import {
    ScheduleConflict,
    categorizeConflicts,
    getOpponentFromGame,
    getOpponentFromSummary,
    getLeagueGameTime,
} from '../utils/scheduleConflictUtils';

interface ScheduleConflictCardProps {
    conflicts: ScheduleConflict[];
}

export const ScheduleConflictCard: React.FC<ScheduleConflictCardProps> = ({ conflicts }) => {
    if (!conflicts || conflicts.length === 0) {
        return null;
    }

    const summary = categorizeConflicts(conflicts);
    const showDetailsList = conflicts.length <= 3;

    return (
        <Paper
            variant="outlined"
            elevation={0}
            data-testid="schedule-conflict-card"
            sx={{
                borderRadius: 2,
                mb: 3,
                overflow: 'hidden',
                borderLeft: '4px solid',
                borderLeftColor: 'warning.main',
            }}
        >
            {/* Header */}
            <Box sx={{ p: { xs: 2, sm: 3 }, pb: 2 }}>
                <Stack direction="row" spacing={1.5} alignItems="center" justifyContent="space-between">
                    <Stack direction="row" spacing={1.5} alignItems="center">
                        <WarningAmberIcon color="warning" />
                        <Typography
                            variant="h5"
                            component="h1"
                            sx={{
                                fontSize: { xs: '1.25rem', sm: '1.5rem' },
                                fontWeight: 600,
                                color: 'text.primary',
                            }}
                        >
                            Schedule Discrepancies
                        </Typography>
                    </Stack>
                    <Chip
                        label={`${conflicts.length} ${conflicts.length === 1 ? 'Issue' : 'Issues'}`}
                        color="warning"
                        size="small"
                        sx={{ fontWeight: 'bold' }}
                    />
                </Stack>
            </Box>

            <Divider />

            {/* Body */}
            <Box sx={{ p: { xs: 2, sm: 3 } }}>
                {showDetailsList ? (
                    <Stack spacing={2} divider={<Divider sx={{ borderStyle: 'dashed' }} />}>
                        {conflicts.map((conflict, idx) => {
                            if (conflict.type === 'discrepancy') {
                                const opponent = getOpponentFromGame(conflict.leagueGame);
                                const leagueDate = getLeagueGameTime(conflict.leagueGame);
                                const leagueTimeStr = leagueDate.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
                                const calDate = new Date(conflict.calendarGame.start);
                                const calTimeStr = calDate.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });

                                return (
                                    <Box key={`conflict-${idx}`}>
                                        <Typography variant="subtitle2" fontWeight="bold" color="text.primary">
                                            Game vs {opponent} ({leagueDate.toLocaleDateString([], { month: 'short', day: 'numeric' })})
                                        </Typography>
                                        <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                                            {conflict.timeMismatch && (
                                                <Stack direction="row" spacing={0.75} alignItems="center">
                                                    <AccessTimeIcon fontSize="inherit" color="warning" sx={{ fontSize: 16 }} />
                                                    <Typography variant="body2" color="text.secondary">
                                                        <strong>Time mismatch:</strong> League: {leagueTimeStr} • Calendar: {calTimeStr}
                                                    </Typography>
                                                </Stack>
                                            )}
                                            {conflict.locationMismatch && (
                                                <Stack direction="row" spacing={0.75} alignItems="center">
                                                    <PlaceIcon fontSize="inherit" color="warning" sx={{ fontSize: 16 }} />
                                                    <Typography variant="body2" color="text.secondary">
                                                        <strong>Location mismatch:</strong> League: {conflict.leagueGame.location} • Calendar: {conflict.calendarGame.location}
                                                    </Typography>
                                                </Stack>
                                            )}
                                        </Stack>
                                    </Box>
                                );
                            }

                            if (conflict.type === 'missing_from_calendar') {
                                const opponent = getOpponentFromGame(conflict.leagueGame);
                                const leagueDate = getLeagueGameTime(conflict.leagueGame);

                                return (
                                    <Box key={`conflict-${idx}`}>
                                        <Typography variant="subtitle2" fontWeight="bold" color="text.primary">
                                            Game vs {opponent} ({leagueDate.toLocaleDateString([], { month: 'short', day: 'numeric' })})
                                        </Typography>
                                        <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 0.5 }}>
                                            <EventBusyIcon fontSize="inherit" color="error" sx={{ fontSize: 16 }} />
                                            <Typography variant="body2" color="text.secondary">
                                                On league schedule, but missing from team calendar.
                                            </Typography>
                                        </Stack>
                                    </Box>
                                );
                            }

                            if (conflict.type === 'missing_from_league') {
                                const opponent = getOpponentFromSummary(conflict.calendarGame.summary) || conflict.calendarGame.summary;
                                const calDate = new Date(conflict.calendarGame.start);

                                return (
                                    <Box key={`conflict-${idx}`}>
                                        <Typography variant="subtitle2" fontWeight="bold" color="text.primary">
                                            Game vs {opponent} ({calDate.toLocaleDateString([], { month: 'short', day: 'numeric' })})
                                        </Typography>
                                        <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 0.5 }}>
                                            <EventRepeatIcon fontSize="inherit" color="info" sx={{ fontSize: 16 }} />
                                            <Typography variant="body2" color="text.secondary">
                                                On team calendar, but not found on league schedule.
                                            </Typography>
                                        </Stack>
                                    </Box>
                                );
                            }

                            if (conflict.type === 'missing_volunteers') {
                                const opponent = getOpponentFromSummary(conflict.calendarGame.summary) || conflict.calendarGame.summary;
                                const calDate = new Date(conflict.calendarGame.start);

                                return (
                                    <Box key={`conflict-${idx}`}>
                                        <Typography variant="subtitle2" fontWeight="bold" color="text.primary">
                                            Game vs {opponent} ({calDate.toLocaleDateString([], { month: 'short', day: 'numeric' })})
                                        </Typography>
                                        <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 0.5 }}>
                                            <VolunteerActivismIcon fontSize="inherit" color="warning" sx={{ fontSize: 16 }} />
                                            <Typography variant="body2" color="text.secondary">
                                                {conflict.missingDuties && conflict.missingDuties.length > 0
                                                    ? `Missing ${conflict.missingDuties.join(' and ')} volunteer assignment.`
                                                    : 'No volunteers assigned for this upcoming game.'}
                                            </Typography>
                                        </Stack>
                                    </Box>
                                );
                            }

                            return null;
                        })}
                    </Stack>
                ) : (
                    /* Summary View for > 3 conflicts */
                    <Stack spacing={1.5}>
                        <Typography variant="body2" color="text.secondary">
                            Found {conflicts.length} discrepancies that need review:
                        </Typography>
                        <Stack spacing={1}>
                            {summary.volunteersCount > 0 && (
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <Chip
                                        label={summary.volunteersCount}
                                        size="small"
                                        color="warning"
                                        sx={{ fontWeight: 'bold', minWidth: 28 }}
                                    />
                                    <Typography variant="body2">
                                        {summary.volunteersCount === 1
                                            ? "1 game doesn't have volunteers assigned"
                                            : `${summary.volunteersCount} games don't have volunteers assigned`}
                                    </Typography>
                                </Stack>
                            )}
                            {summary.mismatchCount > 0 && (
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <Chip
                                        label={summary.mismatchCount}
                                        size="small"
                                        color="warning"
                                        sx={{ fontWeight: 'bold', minWidth: 28 }}
                                    />
                                    <Typography variant="body2">
                                        {summary.mismatchCount === 1
                                            ? "1 game doesn't match the league calendar (time or location)"
                                            : `${summary.mismatchCount} games don't match the league calendar`}
                                    </Typography>
                                </Stack>
                            )}
                            {summary.missingCalendarCount > 0 && (
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <Chip
                                        label={summary.missingCalendarCount}
                                        size="small"
                                        color="warning"
                                        sx={{ fontWeight: 'bold', minWidth: 28 }}
                                    />
                                    <Typography variant="body2">
                                        {summary.missingCalendarCount === 1
                                            ? "1 game is on league schedule but missing from calendar"
                                            : `${summary.missingCalendarCount} games are on league schedule but missing from calendar`}
                                    </Typography>
                                </Stack>
                            )}
                            {summary.missingLeagueCount > 0 && (
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <Chip
                                        label={summary.missingLeagueCount}
                                        size="small"
                                        color="warning"
                                        sx={{ fontWeight: 'bold', minWidth: 28 }}
                                    />
                                    <Typography variant="body2">
                                        {summary.missingLeagueCount === 1
                                            ? "1 game is on calendar but not found on league schedule"
                                            : `${summary.missingLeagueCount} games are on calendar but not found on league schedule`}
                                    </Typography>
                                </Stack>
                            )}
                        </Stack>
                    </Stack>
                )}
            </Box>

            <Divider />

            {/* Actions */}
            <Box
                sx={{
                    px: { xs: 2, sm: 3 },
                    py: 1.5,
                    bgcolor: 'grey.50',
                    display: 'flex',
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: 1,
                }}
            >
                {summary.hasVolunteerIssues && (
                    <Button
                        component={RouterLink}
                        to="/volunteers"
                        variant="outlined"
                        size="small"
                        color="warning"
                        startIcon={<VolunteerActivismIcon />}
                        onClick={() => logEvent('click_view_volunteers_discrepancy')}
                    >
                        View Volunteers
                    </Button>
                )}
                {summary.hasCalendarIssues && (
                    <Button
                        component={RouterLink}
                        to="/calendar"
                        variant="outlined"
                        size="small"
                        color="warning"
                        startIcon={<CalendarMonthIcon />}
                        onClick={() => logEvent('click_view_calendar_discrepancy')}
                    >
                        View Calendar
                    </Button>
                )}
            </Box>
        </Paper>
    );
};

export default ScheduleConflictCard;
